import { createFileRoute, Link, useNavigate } from "@tanstack/react-router";
import { Loader2 } from "lucide-react";
import { useEffect, useState } from "react";
import { Button } from "@/components/ui/button";
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "@/components/ui/card";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { authClient } from "@/lib/auth-client";
import { seo } from "@/utils/seo";

export const Route = createFileRoute("/auth/reset-password")({
  head: () => ({
    title: "Reset Password - Setups by SZESE",
    meta: [
      ...seo({
        title: "Reset Password - Setups by SZESE",
        description: "Reset your Setups by SZESE account password.",
        noindex: true,
      }),
    ],
    links: [
      {
        rel: "canonical",
        href: "https://setupsbyszese.com/auth/reset-password",
      },
    ],
  }),
  component: ResetPasswordPage,
});

function ResetPasswordPage() {
  const navigate = useNavigate();
  const [password, setPassword] = useState("");
  const [confirmPassword, setConfirmPassword] = useState("");
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [success, setSuccess] = useState(false);
  const [isValidToken, setIsValidToken] = useState<boolean | null>(null);

  useEffect(() => {
    // Check if we have a valid token in the URL hash or query params
    // Supabase sends the access token in the URL hash after password reset
    const hash = window.location.hash;
    const params = new URLSearchParams(window.location.search);

    // Check for access_token in hash or query params
    const hasToken = hash.includes("access_token") || params.has("access_token");
    setIsValidToken(hasToken);
  }, []);

  const handleResetPassword = async (e: React.FormEvent) => {
    e.preventDefault();

    if (!password || !confirmPassword) {
      setError("Please enter both password fields.");
      return;
    }

    if (password !== confirmPassword) {
      setError("Passwords do not match.");
      return;
    }

    if (password.length < 8) {
      setError("Password must be at least 8 characters.");
      return;
    }

    setIsLoading(true);
    setError(null);

    try {
      const { error: err } = await authClient.updatePassword(password);
      if (err) {
        setError(err.message ?? "Failed to reset password. The link may have expired.");
      } else {
        setSuccess(true);
        // Redirect to sign-in after a short delay
        setTimeout(() => {
          navigate({ to: "/", search: { redirect: undefined } });
        }, 2000);
      }
    } catch {
      setError("Something went wrong. Please try again.");
    } finally {
      setIsLoading(false);
    }
  };

  const backLink = (
    <div className="absolute top-4 left-4">
      <Link
        to="/"
        className="text-sm text-muted-foreground hover:text-foreground transition-colors"
      >
        &larr; Back to Setups by SZESE
      </Link>
    </div>
  );

  // Loading state while checking token
  if (isValidToken === null) {
    return (
      <div className="relative min-h-screen flex items-center justify-center bg-background p-4">
        {backLink}
        <h1 className="sr-only">Reset your Setups by SZESE password</h1>
        <Card className="w-full max-w-md">
          <CardContent className="pt-6">
            <div className="flex justify-center">
              <Loader2 className="size-8 animate-spin text-muted-foreground" />
            </div>
          </CardContent>
        </Card>
      </div>
    );
  }

  // Invalid or missing token
  if (!isValidToken) {
    return (
      <div className="relative min-h-screen flex items-center justify-center bg-background p-4">
        {backLink}
        <h1 className="sr-only">Reset your Setups by SZESE password</h1>
        <Card className="w-full max-w-md">
          <CardHeader className="text-center">
            <CardTitle className="text-2xl font-bold">Invalid Reset Link</CardTitle>
            <CardDescription>This password reset link is invalid or has expired.</CardDescription>
          </CardHeader>
          <CardContent>
            <Button onClick={() => navigate({ to: "/" })} className="w-full">
              Go to Sign In
            </Button>
          </CardContent>
        </Card>
      </div>
    );
  }

  // Success state
  if (success) {
    return (
      <div className="relative min-h-screen flex items-center justify-center bg-background p-4">
        {backLink}
        <h1 className="sr-only">Reset your Setups by SZESE password</h1>
        <Card className="w-full max-w-md">
          <CardHeader className="text-center">
            <CardTitle className="text-2xl font-bold text-green-600">Password Reset!</CardTitle>
            <CardDescription>
              Your password has been successfully reset. Redirecting to sign in...
            </CardDescription>
          </CardHeader>
        </Card>
      </div>
    );
  }

  // Password reset form
  return (
    <div className="relative min-h-screen flex items-center justify-center bg-background p-4">
      {backLink}
      <h1 className="sr-only">Reset your Setups by SZESE password</h1>
      <Card className="w-full max-w-md">
        <CardHeader className="text-center">
          <CardTitle className="text-2xl font-bold">Set New Password</CardTitle>
          <CardDescription>Enter your new password below</CardDescription>
        </CardHeader>

        <CardContent>
          <form onSubmit={handleResetPassword} className="space-y-4">
            <div>
              <Label htmlFor="new-password">New Password</Label>
              <Input
                id="new-password"
                type="password"
                value={password}
                onChange={(e) => setPassword(e.target.value)}
                placeholder="At least 8 characters"
                required
                minLength={8}
              />
            </div>
            <div>
              <Label htmlFor="confirm-password">Confirm Password</Label>
              <Input
                id="confirm-password"
                type="password"
                value={confirmPassword}
                onChange={(e) => setConfirmPassword(e.target.value)}
                placeholder="Confirm your password"
                required
                minLength={8}
              />
            </div>

            {error && <p className="text-sm text-destructive">{error}</p>}

            <Button type="submit" className="w-full" disabled={isLoading}>
              {isLoading && <Loader2 className="size-4 animate-spin mr-2" />}
              Reset Password
            </Button>
          </form>
        </CardContent>
      </Card>
    </div>
  );
}
